import React from 'react';
import styles from './styles.module.scss';
import { ProductType } from '../../types';
import { generateSavingsSummary } from '../../config/savingsConfig';
import { formatCurrency } from '../../utils/helpers';

interface SavingsSummaryRow {
  label: string;
  value: string;
  scadenza?: string | null;
  category?: 'PV' | 'HP';
  isCo2?: boolean;
}

interface SavingsSummaryProps {
  productType: ProductType;
  zipFlag: boolean | null;
  zipCode?: string;
}

function formatExpiryDate(item: SavingsSummaryRow): string {
  if (item.scadenza) {
    const parts = item.scadenza.split('.');
    if (parts.length === 3) {
      return `${parts[0]}/${parts[1]}/${parts[2]}`;
    }
    return item.scadenza;
  }
  if (item.label === 'PNRR CER Enpal') {
    return '22/11/2026';
  }
  return '-';
}

const SavingsSummary: React.FC<SavingsSummaryProps> = ({ productType, zipFlag, zipCode }) => {
  const summary = generateSavingsSummary(productType, zipFlag, zipCode);

  const isCombo = productType === 'Photovoltaic + Heatpump';
  const pvItems = isCombo
    ? summary.items.filter((item: SavingsSummaryRow) => item.category === 'PV' && !item.isCo2)
    : [];
  const hpItems = isCombo
    ? summary.items.filter((item: SavingsSummaryRow) => item.category === 'HP' && !item.isCo2)
    : [];
  const uncategorizedItems = isCombo
    ? summary.items.filter((item: SavingsSummaryRow) => !item.category && !item.isCo2)
    : summary.items.filter((item: SavingsSummaryRow) => !item.isCo2);

  return (
    <div className={styles.savingsSummaryBox}>
      <p className={styles.summaryTitle}>Risparmio in 10 anni:</p>

      <div className={styles.savingsHeader}>
        <span className={styles.headerLabel}>VANTAGGIO</span>
        <span className={styles.headerValue}>IMPORTO</span>
        <span className={styles.headerExpiry}>SCADENZA</span>
      </div>

      {isCombo && pvItems.length > 0 && (
        <>
          <div className={styles.categoryDivider}>FOTOVOLTAICO</div>
          {pvItems.map((item, index) => (
            <div key={`pv-${index}`} className={styles.savingsItem}>
              <span className={styles.itemLabel}>{item.label}</span>
              <span className={styles.itemValue}>{item.value}</span>
              <span className={styles.itemExpiry}>{formatExpiryDate(item)}</span>
            </div>
          ))}
        </>
      )}

      {isCombo && hpItems.length > 0 && (
        <>
          <div className={styles.categoryDivider}>POMPA DI CALORE</div>
          {hpItems.map((item, index) => (
            <div key={`hp-${index}`} className={styles.savingsItem}>
              <span className={styles.itemLabel}>{item.label}</span>
              <span className={styles.itemValue}>{item.value}</span>
              <span className={styles.itemExpiry}>{formatExpiryDate(item)}</span>
            </div>
          ))}
        </>
      )}

      {uncategorizedItems.map((item, index) => (
        <div key={index} className={styles.savingsItem}>
          <span className={styles.itemLabel}>{item.label}</span>
          <span className={styles.itemValue}>{item.value}</span>
          <span className={styles.itemExpiry}>{formatExpiryDate(item)}</span>
        </div>
      ))}

      <div className={styles.savingsSeparator} />

      <div className={styles.savingsTotal}>
        <span className={styles.itemLabel}>Risparmio Totale</span>
        <span className={styles.itemValue}>{formatCurrency(summary.total)}</span>
        <span className={styles.itemExpiry}></span>
      </div>

      <div className={styles.savingsItem}>
        <span className={styles.itemLabel}>Guadagni Trading Energia</span>
        <span
          className={`${styles.itemValue} ${
            summary.co2Saved === 'Scoprilo in chiamata' ? styles.redCallText : ''
          }`}
        >
          {summary.co2Saved}
        </span>
        <span className={styles.itemExpiry}></span>
      </div>
    </div>
  );
};

export default SavingsSummary;
